import React, { useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { Info, X } from 'react-feather';

import styles from './Step3_FinalReview.module.scss';

const TABS = [
  'Things to do',
  'Food & Drink',
  'Shopping',
  'Self Care',
  'Services',
];

const LIBRARY_IMAGES = [
  {
    id: 't1',
    label: 'Things to do',
    url: 'https://picsum.photos/seed/todo1/800/800',
  },
  {
    id: 't2',
    label: 'Things to do',
    url: 'https://picsum.photos/seed/todo2/800/800',
  },
  {
    id: 'f1',
    label: 'Food & Drink',
    url: 'https://picsum.photos/seed/food1/800/800',
  },
  {
    id: 'f2',
    label: 'Food & Drink',
    url: 'https://picsum.photos/seed/food2/800/800',
  },
  {
    id: 's1',
    label: 'Shopping',
    url: 'https://picsum.photos/seed/shop1/800/800',
  },
  {
    id: 's2',
    label: 'Shopping',
    url: 'https://picsum.photos/seed/shop2/800/800',
  },
  {
    id: 'c1',
    label: 'Self Care',
    url: 'https://picsum.photos/seed/selfcare1/800/800',
  },
  {
    id: 'c2',
    label: 'Self Care',
    url: 'https://picsum.photos/seed/selfcare2/800/800',
  },
  {
    id: 'sv1',
    label: 'Services',
    url: 'https://picsum.photos/seed/services1/800/800',
  },
  {
    id: 'sv2',
    label: 'Services',
    url: 'https://picsum.photos/seed/services2/800/800',
  },
];

function prettifyGoal(goalKey) {
  if (goalKey === 'attract_new_customers') return 'Attract New Customers';
  if (goalKey === 'increase_sales') return 'Increase Sales';
  if (goalKey === 'fill_slow_periods') return 'Fill Slow Periods';
  return '—';
}

function prettifyImpact(impactKey) {
  if (impactKey === 'steady_growth') return 'Steady Growth';
  if (impactKey === 'quick_boost') return 'Quick Boost';
  if (impactKey === 'maximum_impact') return 'Maximum Impact';
  return '—';
}

const INFO_MAP = {
  Goal: 'The primary objective you selected for this coupon.',
  'Item Type':
    'Whether this applies to a single item, a line of items, or storewide.',
  Title: 'The main offering title used to personalize recommendations.',
  Description:
    'Extra context to help customers and improve AI recommendations.',
  Price: 'Optional reference price for your item/service.',
  Category:
    'Used to pre-filter the image library and personalize the experience.',
  'Slow Days': 'Used to pre-fill day-of-week rules for slow-period promotions.',
  'Slow Times':
    'Used to pre-fill time-of-day rules for slow-period promotions.',
  'Desired Impact': 'Controls how aggressive the recommendation should feel.',
  'Recommended Type': 'Style bucket of the recommended coupon.',
  'Coupon Headline': 'What customers will see first on the coupon.',
  Details: 'Supporting details shown under the headline in the app.',
};

function normalizeCategoryGuess(label) {
  const v = (label || '').toLowerCase();
  if (v.includes('food')) return 'Food & Drink';
  if (v.includes('drink')) return 'Food & Drink';
  if (v.includes('shop')) return 'Shopping';
  if (v.includes('self')) return 'Self Care';
  if (v.includes('care')) return 'Self Care';
  if (v.includes('service')) return 'Services';
  if (v.includes('thing')) return 'Things to do';
  return 'Food & Drink';
}

export default function Step3_FinalReview({
  draft,
  onBack,
  onSelectRecommendation,
  onSetImage,
  onToggleTerms,
  onPublish,
  onEditCoupon,
  onSetStage,
}) {
  const recs = Array.isArray(draft.recommendations)
    ? draft.recommendations
    : [];
  const selected = draft.selectedRecommendation || recs[0] || null;

  const stage = draft.step3Stage || 'pick';
  const fileInputRef = useRef(null);

  const [tab, setTab] = useState(
    normalizeCategoryGuess(draft.offering?.categoryLabel),
  );
  const [uploadError, setUploadError] = useState('');

  const filteredImages = useMemo(
    () => LIBRARY_IMAGES.filter(i => i.label === tab),
    [tab],
  );

  const heroTitle = useMemo(() => {
    if (draft.goalKey === 'attract_new_customers')
      return 'Choose Your Perfect Coupon: Designed for Your Success!';
    if (draft.goalKey === 'increase_sales')
      return 'Choose Your Perfect Coupon: Designed for Your Success!';
    if (draft.goalKey === 'fill_slow_periods')
      return 'Choose Your Perfect Coupon: Designed for Your Success!';
    return 'Choose Your Perfect Coupon: Designed for Your Success!';
  }, [draft.goalKey]);

  const heroSub = useMemo(() => {
    return 'Our AI has generated high-converting offers based on your goals. Select the one that best fits your vision — you can fine-tune later.';
  }, []);

  const finalTitle = useMemo(() => {
    if (draft.goalKey === 'attract_new_customers') {
      return 'Your Coupon is Ready to Drive New Customers!';
    }
    if (draft.goalKey === 'increase_sales') {
      return 'Your Coupon is Ready to Boost Sales!';
    }
    if (draft.goalKey === 'fill_slow_periods') {
      return 'Your Coupon is Ready to Optimize Your Schedule and Fill Those Slow Periods!';
    }
    return 'Your Coupon is Ready!';
  }, [draft.goalKey]);

  const finalSub = useMemo(() => {
    return "Click 'Publish' now — customers are waiting to discover your deal!";
  }, []);

  const summaryPairs = useMemo(() => {
    const out = [];
    if (draft.goalKey) out.push(['Goal', prettifyGoal(draft.goalKey)]);
    if (draft.offering?.itemType)
      out.push(['Item Type', draft.offering.itemType]);
    if (draft.offering?.itemName) out.push(['Title', draft.offering.itemName]);
    if (draft.offering?.itemDescription)
      out.push(['Description', draft.offering.itemDescription]);
    if (draft.offering?.price) out.push(['Price', `$${draft.offering.price}`]);
    if (draft.offering?.categoryLabel)
      out.push(['Category', draft.offering.categoryLabel]);
    if (draft.offering?.slowDays?.length)
      out.push(['Slow Days', draft.offering.slowDays.join(', ')]);
    if (draft.offering?.slowTimes?.length)
      out.push(['Slow Times', draft.offering.slowTimes.join(', ')]);
    if (draft.impactKey)
      out.push(['Desired Impact', prettifyImpact(draft.impactKey)]);
    if (selected?.type) out.push(['Recommended Type', selected.type]);
    if (selected?.headline) out.push(['Coupon Headline', selected.headline]);
    if (selected?.details) out.push(['Details', selected.details]);
    return out;
  }, [draft, selected]);

  const canPublish = Boolean(draft.termsAccepted && selected);

  const onPickRec = r => {
    onSelectRecommendation(r);
    onSetStage('image');
  };

  const handleUpload = file => {
    setUploadError('');
    if (!file) return;

    const maxBytes = 2 * 1024 * 1024; // 2MB
    if (file.size > maxBytes) {
      setUploadError('Please upload an image under 2MB.');
      return;
    }

    const url = URL.createObjectURL(file);
    onSetImage(url);
  };

  // -------------------------
  // STAGE: PICK (Page 4)
  // -------------------------
  if (stage === 'pick' || stage === 'image') {
    return (
      <div className={styles.page}>
        <div className={styles.hero}>
          <div className={styles.heroTitle}>{heroTitle}</div>
          <div className={styles.heroSub}>{heroSub}</div>
        </div>

        <div className={styles.pickPanel}>
          <div className={styles.panelTitle}>Recommended Coupons</div>

          <div className={styles.recsRow}>
            {recs.slice(0, 3).map((r, idx) => {
              const active = selected?.id === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  className={`${styles.recBox} ${
                    active ? styles.recBoxActive : ''
                  }`}
                  onClick={() => onPickRec(r)}
                  style={{ '--stagger': `${idx * 90}ms` }}
                >
                  <div className={styles.recBoxTop}>
                    <div
                      className={styles.recBoxTitle}
                    >{`Recommended Coupon #${idx + 1}`}</div>
                    {active && <div className={styles.badge}>Selected</div>}
                  </div>

                  <div className={styles.recBoxLine}>
                    <strong>Type:</strong> {r.type || '—'}
                  </div>
                  <div className={styles.recBoxLine}>
                    <strong>Value:</strong> {r.reward?.label || '—'}
                  </div>

                  <div className={styles.recBoxHeadline}>
                    {r.headline || '—'}
                  </div>
                  <div className={styles.recBoxDetails}>{r.details || '—'}</div>
                </button>
              );
            })}
          </div>

          <div className={styles.pickBackRow}>
            <button type="button" className={styles.ghostBtn} onClick={onBack}>
              ← Back to inputs
            </button>
          </div>
        </div>

        {/* -------------------------
            IMAGE MODAL (Flowchart pop-up)
        -------------------------- */}
        {stage === 'image' && (
          <div className={styles.modalOverlay} role="dialog" aria-modal="true">
            <div className={styles.modal}>
              <div className={styles.modalHead}>
                <div>
                  <div className={styles.modalTitle}>
                    Choose an Attractive Image for Your Coupon, or Upload Your
                    Own!
                  </div>
                  <div className={styles.modalSub}>
                    Pre-filtered by your category. You can skip this step and
                    continue with a text-only coupon.
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.iconClose}
                  onClick={() => onSetStage('pick')}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div
                className={styles.tabRow}
                role="tablist"
                aria-label="Image categories"
              >
                {TABS.map(t => {
                  const active = t === tab;
                  return (
                    <button
                      key={t}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      className={`${styles.tab} ${
                        active ? styles.tabActive : ''
                      }`}
                      onClick={() => setTab(t)}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>

              <div className={styles.imageGrid}>
                <button
                  type="button"
                  className={styles.uploadTile}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className={styles.uploadTitle}>Upload</div>
                  <div className={styles.uploadSub}>from Desktop</div>
                  <div className={styles.uploadNote}>Max 2MB</div>

                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      handleUpload(f);
                    }}
                  />
                </button>

                {filteredImages.slice(0, 8).map(img => (
                  <button
                    key={img.id}
                    type="button"
                    className={`${styles.imageTile} ${
                      draft.image === img.url ? styles.imageTileActive : ''
                    }`}
                    onClick={() => {
                      setUploadError('');
                      onSetImage(img.url);
                    }}
                  >
                    <img src={img.url} alt={img.label} />
                    {draft.image === img.url && (
                      <div className={styles.imageBadge}>Selected</div>
                    )}
                  </button>
                ))}
              </div>

              {uploadError && (
                <div className={styles.uploadError}>{uploadError}</div>
              )}

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => {
                    onSetImage(null);
                    onSetStage('review');
                  }}
                >
                  Skip for Now
                </button>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => onSetStage('review')}
                  disabled={!selected}
                  aria-disabled={!selected}
                >
                  Review Your Coupon
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // -------------------------
  // STAGE: REVIEW (Final Review)
  // -------------------------
  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.heroTitle}>{finalTitle}</div>
        <div className={styles.heroSub}>{finalSub}</div>
      </div>

      <div className={styles.grid}>
        <div className={styles.panel}>
          <div className={styles.panelTitle}>Coupon settings breakdown</div>

          <div className={styles.kvList}>
            {summaryPairs.map(([k, v]) => (
              <div key={k} className={styles.kvRow}>
                <div className={styles.k}>
                  {k}
                  <span
                    className={styles.infoIcon}
                    title={INFO_MAP[k] || 'Info'}
                  >
                    <Info size={14} />
                  </span>
                </div>
                <div className={styles.v}>{v}</div>
              </div>
            ))}
          </div>

          <div className={styles.actionsTop}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => onSetStage('pick')}
            >
              Change Recommendation
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => onSetStage('image')}
            >
              Change Image
            </button>
          </div>

          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={!!draft.termsAccepted}
              onChange={e => onToggleTerms(e.target.checked)}
            />
            <span>
              By publishing this coupon, you are agreeing to our{' '}
              <a
                href="/terms-of-service/default"
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>
            </span>
          </label>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={onBack}
            >
              Back
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={onEditCoupon}
            >
              Edit Coupon
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onPublish}
              disabled={!canPublish}
              aria-disabled={!canPublish}
            >
              Publish Coupon
            </button>
          </div>
        </div>

        <div className={styles.previewWrap}>
          <div className={styles.phone}>
            <div className={styles.phoneTop}>Coupon Mock Up</div>

            <div className={styles.previewCard}>
              {draft.image ? (
                <img
                  className={styles.previewImage}
                  src={draft.image}
                  alt="Coupon visual"
                />
              ) : (
                <div className={styles.noImage}>No Image Selected</div>
              )}

              <div className={styles.previewBody}>
                <div className={styles.previewHeadline}>
                  {selected?.headline || 'Your Coupon Headline'}
                </div>
                <div className={styles.previewDetails}>
                  {selected?.details || 'Coupon details will appear here.'}
                </div>
                <div className={styles.previewMeta}>
                  <span>{prettifyGoal(draft.goalKey)}</span>
                  <span>•</span>
                  <span>{prettifyImpact(draft.impactKey)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.helper}>
            This is a non-editable preview. Click <strong>Edit Coupon</strong>{' '}
            to customize advanced settings.
          </div>
        </div>
      </div>
    </div>
  );
}

Step3_FinalReview.propTypes = {
  draft: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onSelectRecommendation: PropTypes.func.isRequired,
  onSetImage: PropTypes.func.isRequired,
  onToggleTerms: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  onEditCoupon: PropTypes.func.isRequired,
  onSetStage: PropTypes.func.isRequired,
};
